// =============================================================================
// MarketingScreen — campagnes samenstellen en versturen naar opted-in klanten
// =============================================================================
// Drie sub-weergaven in één screen: lijst → editor (draft) → detail
// (sending/sent, met live voortgang + Resend-statistieken). Alle data loopt
// via de booking-campaign-send edge function; deze pagina is gegate op de
// booking_marketing permission (route + server-side).
//
// Editor: per taal (NL / optioneel EN) één variant, elk in 'Blokken'-modus
// (gestructureerde editor) of 'HTML'-modus (geplakte e-mail-HTML met tokens
// {{voornaam}} {{achternaam}} {{email}} {{unsubscribe_url}}). Rechts een live
// voorbeeld: de SERVER rendert (preview action) precies wat een echte
// verzending zou opleveren — inclusief automatisch toegevoegde footer als de
// HTML geen {{unsubscribe_url}} bevat.
//
// Verstuurregels die de server afdwingt en de UI dus ook toont:
//  * eerst een testmail naar jezelf, dan pas "Versturen"
//  * één campagne tegelijk in 'sending'
//  * de doelgroep is altijd marketing_opt_in minus bounces/klachten — de
//    filters hier versmallen alleen verder.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { formatVenueDateTime } from '../lib/time';
import { fetchWalkinRefData, type BookingLocation } from '../lib/walkin-data';
import { FollowupTab } from './FollowupTab';
import { GoogleReviewsTab } from './GoogleReviewsTab';
import { ServiceMailsTab } from './ServiceMailsTab';
import {
  audienceCount,
  cancelCampaign,
  createCampaign,
  deleteCampaign,
  getCampaign,
  duplicateCampaign,
  listCampaigns,
  previewCampaign,
  scheduleCampaign,
  startCampaign,
  testSendCampaign,
  unscheduleCampaign,
  updateCampaign,
  AUDIENCE_DEFAULTS,
  type AudienceSummary,
  type CampaignAudience,
  type CampaignHeld,
  type CampaignBlock,
  type CampaignContent,
  type CampaignDetail,
  type CampaignDraftInput,
  type CampaignListRow,
  type CampaignLocale,
  type CampaignStats,
  type CampaignVariant,
  type MarketingError,
} from '../api/marketing';

type MarketingTab = 'campaigns' | 'followup' | 'reviews' | 'service';

type View =
  | { kind: 'list' }
  | { kind: 'edit'; campaignId: string | null } // null = nieuwe campagne
  | { kind: 'detail'; campaignId: string };

const STATUS_LABEL: Record<string, string> = {
  draft: 'Concept',
  scheduled: 'Ingepland',
  sending: 'Versturen…',
  sent: 'Verzonden',
  cancelled: 'Geannuleerd',
};

const ERROR_LABEL: Record<string, string> = {
  permission_denied: 'Je hebt geen rechten voor marketing-campagnes.',
  test_send_required: 'Stuur eerst een testmail naar jezelf voordat je verstuurt.',
  another_campaign_sending: 'Er wordt al een andere campagne verstuurd. Wacht tot die klaar is.',
  campaign_audience_empty: 'De doelgroep is leeg met deze filters.',
  campaign_not_draft: 'Deze campagne is geen concept meer.',
  unsubscribe_key_missing: 'UNSUBSCRIBE_SECRET ontbreekt op de server.',
  invalid_content: 'De inhoud is niet compleet.',
  invalid_name: 'De interne naam mag maximaal 120 tekens zijn.',
  invalid_test_address: 'Vul een geldig e-mailadres in voor de testmail.',
  invalid_scheduled_at: 'Kies een datum en tijd om te versturen.',
  scheduled_at_in_past: 'Het verzendmoment moet in de toekomst liggen.',
  scheduled_at_too_far: 'Plan hooguit een jaar vooruit.',
  campaign_not_scheduled: 'Deze campagne is niet (meer) ingepland.',
  // A server from before the scheduled start (the admin can be ahead of it).
  unknown_action: 'Inplannen is nog niet beschikbaar op de server. Verstuur nu, of probeer het later.',
};

function errText(e: MarketingError | null): string {
  if (!e) return 'Onbekende fout';
  const base = ERROR_LABEL[e.error] ?? e.error;
  return e.error === 'invalid_content' && e.message ? `${base} (${e.message})` : (ERROR_LABEL[e.error] ?? e.message ?? e.error);
}

// `locations` lets the label print a venue NAME instead of a uuid. It is
// optional because the list view renders before the screen's venue fetch has
// landed; until then the filter still has to read as something, so fall back
// to a neutral "vestiging" rather than flashing a raw uuid.
function audienceLabel(a: CampaignAudience, locations?: BookingLocation[]): string {
  const parts: string[] = [];
  parts.push(a.locale === 'nl' ? 'Nederlands' : a.locale === 'en' ? 'Engels' : 'Alle talen');
  if (a.location_id) {
    const loc = locations?.find((l) => l.id === a.location_id);
    parts.push(loc ? (loc.display_name ?? loc.name) : 'vestiging');
  } else {
    parts.push('alle vestigingen');
  }
  if (a.booked_since) parts.push(`geboekt sinds ${a.booked_since}`);
  const excluded = a.exclude_campaign_ids?.length ?? 0;
  if (excluded > 0) {
    parts.push(excluded === 1 ? 'excl. 1 eerdere campagne' : `excl. ${excluded} eerdere campagnes`);
  }
  return parts.join(' · ');
}

// The spacing rules with the server defaults filled in, so a draft saved
// before the rules existed reads the way the server will treat it.
function spacing(a: CampaignAudience) {
  return {
    recent_gap_days: a.recent_gap_days ?? AUDIENCE_DEFAULTS.recent_gap_days,
    hold_first_visit: a.hold_first_visit ?? AUDIENCE_DEFAULTS.hold_first_visit,
    first_visit_delay_days: a.first_visit_delay_days ?? AUDIENCE_DEFAULTS.first_visit_delay_days,
    defer_held: a.defer_held ?? AUDIENCE_DEFAULTS.defer_held,
  };
}

function heldLine(s: AudienceSummary, a: CampaignAudience): string {
  const parts: string[] = [];
  if (s.held_recent) parts.push(`${s.held_recent} kregen recent al een marketingmail`);
  if (s.held_first_visit) parts.push(`${s.held_first_visit} hebben hun eerste bezoek nog voor de boeg`);
  const what = spacing(a).defer_held
    ? `krijgen de mail later${s.last_hold_until ? ` (de laatste rond ${formatVenueDateTime(s.last_hold_until)})` : ''}`
    : 'worden niet gemaild';
  return `${parts.join(', ')}: ${what}.`;
}

function audienceCountLine(s: AudienceSummary, a: CampaignAudience): string {
  if (s.count === s.ready) return `Doelgroep: ${s.count} ontvangers.`;
  return `Doelgroep: ${s.count}, waarvan ${s.ready} direct. ${heldLine(s, a)}`;
}

// Keeps marketing mails apart and leaves first-time visitors alone until after
// their visit. "Recent" counts campaigns AND the automatic post-visit mails;
// booking confirmations and reminders never count.
function SpacingControls({
  audience,
  onChange,
}: {
  audience: CampaignAudience;
  onChange: (patch: Partial<CampaignAudience>) => void;
}) {
  const s = spacing(audience);
  const days = (v: string) => Math.max(0, Math.min(365, Math.round(Number(v) || 0)));
  return (
    <div className="marketing-spacing">
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Niet mailen als iemand de laatste … dagen een marketingmail kreeg (0 = uit)</span>
          <input
            type="number"
            min={0}
            max={365}
            value={s.recent_gap_days}
            onChange={(e) => onChange({ recent_gap_days: days(e.target.value) })}
          />
        </label>
        <label className="walkin-field">
          <span>
            <input
              type="checkbox"
              checked={s.hold_first_visit}
              onChange={(e) => onChange({ hold_first_visit: e.target.checked })}
            />{' '}
            Nieuwe klanten met een bezoek nog voor de boeg overslaan, tot … dagen na dat bezoek
          </span>
          <input
            type="number"
            min={0}
            max={365}
            value={s.first_visit_delay_days}
            disabled={!s.hold_first_visit}
            onChange={(e) => onChange({ first_visit_delay_days: days(e.target.value) })}
          />
        </label>
      </div>
      <fieldset className="marketing-spacing-mode">
        <legend>Wat met deze mensen?</legend>
        <label>
          <input
            type="radio"
            name="defer_held"
            checked={!s.defer_held}
            onChange={() => onChange({ defer_held: false })}
          />{' '}
          Helemaal niet mailen met deze campagne
        </label>
        <label>
          <input
            type="radio"
            name="defer_held"
            checked={s.defer_held}
            onChange={() => onChange({ defer_held: true })}
          />{' '}
          Later mailen, zodra de wachttijd voorbij is
        </label>
      </fieldset>
    </div>
  );
}

function CampaignHeldNote({ held }: { held: CampaignHeld | null }) {
  if (!held) return null;
  const parts: string[] = [];
  if (held.skipped_recently_mailed) parts.push(`${held.skipped_recently_mailed} niet gemaild (recent al gemaild)`);
  if (held.skipped_upcoming_first_visit) {
    parts.push(`${held.skipped_upcoming_first_visit} niet gemaild (eerste bezoek nog voor de boeg)`);
  }
  if (held.waiting) {
    parts.push(
      `${held.waiting} wachten op hun beurt` +
        (held.waiting_until ? `, de laatste rond ${formatVenueDateTime(held.waiting_until)}` : ''),
    );
  }
  if (parts.length === 0) return null;
  return <p className="muted">{parts.join(' · ')}</p>;
}

export function MarketingScreen() {
  // Own venue list, fetched once and handed down. Deliberately NOT the header's
  // LocationProvider: (1) that list hides booking_enabled=false venues, and a
  // venue closed for online booking is still a valid mailing audience; (2) a
  // campaign's audience is a property of the campaign, saved with it, so the
  // header's global selection must never silently re-scope a draft.
  const [locations, setLocations] = useState<BookingLocation[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchWalkinRefData(true)
      .then((ref) => { if (!cancelled) setLocations(ref.locations); })
      .catch(() => { /* audience label falls back to a neutral word; the select stays empty */ });
    return () => { cancelled = true; };
  }, []);
  // Twee tabbladen op één pagina, achter dezelfde booking_marketing-permissie:
  // "Campagnes" is de mail die jij verstuurt, "Opvolging" de mail die vanzelf
  // gaat nadat iemand bij ons was. Ze delen de afzender, de opt-in-regel en de
  // uitschrijflink, dus ze horen bij elkaar.
  const [tab, setTab] = useState<MarketingTab>('campaigns');
  const [view, setView] = useState<View>({ kind: 'list' });
  const [rows, setRows] = useState<CampaignListRow[]>([]);
  const [fromAddress, setFromAddress] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);

  // Duplicating opens the copy straight away: the point is always to edit it
  // (usually only the audience) and send it, not to admire it in the list.
  const handleDuplicate = useCallback(async (id: string) => {
    setDuplicating(id);
    setError(null);
    const res = await duplicateCampaign(id);
    setDuplicating(null);
    if (res.ok && res.data) setView({ kind: 'edit', campaignId: res.data.campaign_id });
    else setError(errText(res.error));
  }, []);

  const loadList = useCallback(async () => {
    setError(null);
    const res = await listCampaigns();
    if (res.ok && res.data) {
      setRows(res.data.campaigns);
      setFromAddress(res.data.from_address);
    } else {
      setError(errText(res.error));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'campaigns' && view.kind === 'list') {
      setLoading(true);
      void loadList();
    }
  }, [tab, view, loadList]);

  if (tab === 'followup') {
    return (
      <div className="screen marketing-screen">
        <div className="screen-header">
          <h1>Marketing</h1>
        </div>
        <MarketingTabs tab={tab} onChange={setTab} />
        <FollowupTab locations={locations} />
      </div>
    );
  }

  if (tab === 'service') {
    return (
      <div className="screen marketing-screen">
        <div className="screen-header">
          <h1>Marketing</h1>
        </div>
        <MarketingTabs tab={tab} onChange={setTab} />
        <ServiceMailsTab locations={locations} />
      </div>
    );
  }

  if (tab === 'reviews') {
    return (
      <div className="screen marketing-screen">
        <div className="screen-header">
          <h1>Marketing</h1>
        </div>
        <MarketingTabs tab={tab} onChange={setTab} />
        <GoogleReviewsTab />
      </div>
    );
  }

  if (view.kind === 'edit') {
    return (
      <CampaignEditor
        campaignId={view.campaignId}
        locations={locations}
        campaigns={rows}
        onClose={() => setView({ kind: 'list' })}
        onStarted={(id) => setView({ kind: 'detail', campaignId: id })}
      />
    );
  }
  if (view.kind === 'detail') {
    return (
      <CampaignDetailView
        campaignId={view.campaignId}
        locations={locations}
        onClose={() => setView({ kind: 'list' })}
        onEdit={(id) => setView({ kind: 'edit', campaignId: id })}
      />
    );
  }

  return (
    <div className="screen marketing-screen">
      <div className="screen-header">
        <h1>Marketing</h1>
      </div>
      <MarketingTabs tab={tab} onChange={setTab} />
      <div className="vouchers-toolbar">
        <p className="muted">
          Campagnes gaan alleen naar klanten met marketing opt-in (min bounces en klachten).
          {fromAddress ? <> Afzender: <strong>{fromAddress}</strong>.</> : null}
        </p>
        <div className="vouchers-toolbar-actions">
          <button className="walkin-primary-btn" onClick={() => setView({ kind: 'edit', campaignId: null })}>
            Nieuwe campagne
          </button>
        </div>
      </div>
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <p className="muted">Laden…</p> : null}
      {!loading && rows.length === 0 && !error ? (
        <p className="muted vouchers-empty">Nog geen campagnes.</p>
      ) : null}
      {rows.length > 0 ? (
        <table className="vouchers-table marketing-table">
          <thead>
            <tr>
              <th>Campagne</th>
              <th>Talen</th>
              <th>Status</th>
              <th>Doelgroep</th>
              <th>Verzonden</th>
              <th>Datum</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className="marketing-row"
                onClick={() =>
                  setView(
                    r.status === 'draft'
                      ? { kind: 'edit', campaignId: r.id }
                      : { kind: 'detail', campaignId: r.id },
                  )
                }
              >
                <td>
                  <strong>{r.name ?? r.subject}</strong>
                  {r.name ? <div className="muted small">{r.subject}</div> : null}
                </td>
                <td className="muted">{r.locales.map((l) => l.toUpperCase()).join(' + ')}</td>
                <td><span className={`pill marketing-pill-${r.status}`}>{STATUS_LABEL[r.status] ?? r.status}</span></td>
                <td className="muted">{audienceLabel(r.audience, locations)}</td>
                <td>{r.status === 'draft' || r.status === 'scheduled' ? '—' : `${r.sent_count}/${r.recipient_count}`}</td>
                <td className="muted">
                  {r.status === 'scheduled' && r.scheduled_at
                    ? `gepland ${formatVenueDateTime(r.scheduled_at)}`
                    : formatVenueDateTime(r.started_at ?? r.created_at)}
                </td>
                <td>
                  <button
                    className="btn-link"
                    disabled={duplicating != null}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDuplicate(r.id);
                    }}
                  >
                    {duplicating === r.id ? 'Kopiëren…' : 'Dupliceer'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function MarketingTabs({
  tab,
  onChange,
}: {
  tab: MarketingTab;
  onChange: (t: MarketingTab) => void;
}) {
  return (
    <div className="marketing-lang-tabs">
      <button
        className={tab === 'campaigns' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
        onClick={() => onChange('campaigns')}
      >
        Campagnes
      </button>
      <button
        className={tab === 'followup' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
        onClick={() => onChange('followup')}
      >
        Opvolging
      </button>
      <button
        className={tab === 'reviews' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
        onClick={() => onChange('reviews')}
      >
        Reviews
      </button>
      <button
        className={tab === 'service' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
        onClick={() => onChange('service')}
      >
        Servicemails
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor (nieuw + draft)
// ---------------------------------------------------------------------------
function emptyVariant(): CampaignVariant {
  return {
    subject: '',
    preheader: null,
    mode: 'blocks',
    blocks: [
      { type: 'heading', text: 'Hoi {{voornaam}},' },
      { type: 'paragraph', text: '' },
    ],
  };
}

function emptyDraft(): CampaignDraftInput {
  return {
    name: null,
    content: { nl: emptyVariant() },
    default_locale: 'nl',
    audience: { ...AUDIENCE_DEFAULTS },
    per_tick: 60,
  };
}

// Client-side pre-check mirroring the server's validateContent, for friendly
// messages before a round-trip. The server remains authoritative.
function draftProblem(d: CampaignDraftInput): string | null {
  const locs = (['nl', 'en'] as const).filter((l) => d.content[l]);
  if (locs.length === 0) return 'Minimaal één taalvariant is nodig.';
  for (const l of locs) {
    const v = d.content[l]!;
    const label = l.toUpperCase();
    if (!v.subject.trim()) return `${label}: onderwerp is verplicht.`;
    if (v.mode === 'html' && !(v.html ?? '').trim()) return `${label}: plak de HTML van de e-mail.`;
    if (
      v.mode === 'blocks' &&
      (v.blocks ?? []).every((b) => (b.type === 'heading' || b.type === 'paragraph' ? !b.text.trim() : false))
    ) {
      return `${label}: voeg inhoud toe aan de e-mail.`;
    }
  }
  return null;
}

// Strip empty text blocks before sending to the server.
function cleanDraft(d: CampaignDraftInput): CampaignDraftInput {
  const content: CampaignContent = {};
  for (const l of ['nl', 'en'] as const) {
    const v = d.content[l];
    if (!v) continue;
    content[l] = v.mode === 'blocks'
      ? {
        ...v,
        blocks: (v.blocks ?? []).filter(
          (b) => !((b.type === 'heading' || b.type === 'paragraph') && !b.text.trim()),
        ),
      }
      : v;
  }
  const default_locale: CampaignLocale = content[d.default_locale] ? d.default_locale : (content.nl ? 'nl' : 'en');
  return { ...d, content, default_locale };
}

function CampaignEditor({
  campaignId,
  locations,
  campaigns,
  onClose,
  onStarted,
}: {
  campaignId: string | null;
  locations: BookingLocation[];
  // Earlier campaigns, for the "skip whoever already got this" checkboxes.
  // Comes from the list view that got us here, so it is already loaded.
  campaigns: CampaignListRow[];
  onClose: () => void;
  onStarted: (id: string) => void;
}) {
  const { operator } = useAuth();
  // Empty = server default (the logged-in operator). Kept as free text so a
  // proof copy can go to a colleague or a private inbox in another client.
  const [testTo, setTestTo] = useState('');
  // Spam filters score the subject line too, so a placement test needs the
  // option to send the real one instead of the [TEST]-prefixed version.
  const [plainSubject, setPlainSubject] = useState(false);
  const [id, setId] = useState<string | null>(campaignId);
  const [draft, setDraft] = useState<CampaignDraftInput>(emptyDraft);
  const [lang, setLang] = useState<CampaignLocale>('nl');
  const [testSentAt, setTestSentAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(campaignId != null);
  const [busy, setBusy] = useState<string | null>(null); // welke actie loopt
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [count, setCount] = useState<AudienceSummary | null>(null);
  const [confirmStart, setConfirmStart] = useState<AudienceSummary | null>(null); // audience count in modal
  // Send now, or schedule: a datetime-local value in the browser's own time,
  // which for the admin's users is the venue's.
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [scheduleAt, setScheduleAt] = useState('');

  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      const res = await getCampaign(campaignId);
      if (res.ok && res.data) {
        const c = res.data.campaign;
        if (c.status !== 'draft') {
          onStarted(c.id);
          return;
        }
        setDraft({
          name: c.name ?? null,
          content: c.content,
          default_locale: c.default_locale,
          audience: c.audience,
          per_tick: c.per_tick,
        });
        setLang(c.content.nl ? 'nl' : 'en');
        setTestSentAt(c.test_sent_at);
        if (c.start_error) {
          setError(
            `De geplande verzending is niet gestart: ${ERROR_LABEL[c.start_error] ?? c.start_error} ` +
              'De campagne staat weer op concept.',
          );
        }
      } else {
        setError(errText(res.error));
      }
      setLoading(false);
    })();
  }, [campaignId, onStarted]);

  // Doelgroep-telling volgt de filters (licht debounced).
  useEffect(() => {
    setCount(null);
    const t = setTimeout(async () => {
      const res = await audienceCount(draft.audience);
      if (res.ok && res.data) setCount(res.data);
    }, 400);
    return () => clearTimeout(t);
  }, [draft.audience]);

  const variant = draft.content[lang] ?? emptyVariant();
  const setVariant = (v: CampaignVariant) =>
    setDraft((d) => ({ ...d, content: { ...d.content, [lang]: v } }));

  const save = useCallback(async (): Promise<string | null> => {
    setError(null);
    const problem = draftProblem(draft);
    if (problem) {
      setError(problem);
      return null;
    }
    const clean = cleanDraft(draft);
    const res = id ? await updateCampaign(id, clean) : await createCampaign(clean);
    if (!res.ok || !res.data) {
      setError(errText(res.error));
      return null;
    }
    if (!id) setId(res.data.campaign_id);
    return res.data.campaign_id;
  }, [draft, id]);

  const handleSave = async () => {
    setBusy('save');
    const saved = await save();
    if (saved) setNotice('Concept opgeslagen.');
    setBusy(null);
  };

  const handleTest = async () => {
    setBusy('test');
    setNotice(null);
    const saved = await save();
    if (saved) {
      const res = await testSendCampaign(saved, lang, testTo.trim() || undefined, plainSubject);
      if (res.ok && res.data) {
        setTestSentAt(new Date().toISOString());
        setNotice(
          `Testmail (${lang.toUpperCase()}) verstuurd naar ${res.data.to}.` +
            (res.data.sample_names
              ? ' De naam in deze testmail is een voorbeeldwaarde — echte klanten krijgen hun eigen naam.'
              : ' Namen komen uit het klantprofiel van dit adres.'),
        );
      } else {
        setError(errText(res.error));
      }
    }
    setBusy(null);
  };

  const handleStartClick = async () => {
    setBusy('precheck');
    setNotice(null);
    const saved = await save();
    if (saved) {
      const res = await audienceCount(draft.audience);
      if (res.ok && res.data) setConfirmStart(res.data);
      else setError(errText(res.error));
    }
    setBusy(null);
  };

  const handleStartConfirmed = async () => {
    if (!id) return;
    setBusy('start');
    let res;
    if (when === 'later') {
      const at = new Date(scheduleAt);
      if (!scheduleAt || Number.isNaN(at.getTime())) {
        setBusy(null);
        setConfirmStart(null);
        setError(errText({ error: 'invalid_scheduled_at' }));
        return;
      }
      res = await scheduleCampaign(id, at.toISOString());
    } else {
      res = await startCampaign(id);
    }
    setBusy(null);
    setConfirmStart(null);
    if (res.ok) onStarted(id);
    else setError(errText(res.error));
  };

  const handleDelete = async () => {
    if (!id) {
      onClose();
      return;
    }
    if (!window.confirm('Concept verwijderen?')) return;
    setBusy('delete');
    const res = await deleteCampaign(id);
    setBusy(null);
    if (res.ok) onClose();
    else setError(errText(res.error));
  };

  const removeLang = (l: CampaignLocale) => {
    setDraft((d) => {
      const content = { ...d.content };
      delete content[l];
      const remaining: CampaignLocale = content.nl ? 'nl' : 'en';
      return { ...d, content, default_locale: remaining };
    });
    setLang((cur) => (cur === l ? (l === 'nl' ? 'en' : 'nl') : cur));
  };

  if (loading) {
    return <div className="screen marketing-screen"><p className="muted">Laden…</p></div>;
  }

  const hasNl = !!draft.content.nl;
  const hasEn = !!draft.content.en;
  const displaySubject = (draft.content[draft.default_locale] ?? draft.content.nl ?? draft.content.en)?.subject ?? '';

  return (
    <div className="screen marketing-screen">
      <div className="screen-header">
        <h1>{id ? 'Campagne bewerken' : 'Nieuwe campagne'}</h1>
      </div>
      <button className="btn-link back-link" onClick={onClose}>← Terug naar overzicht</button>

      <section className="marketing-panel">
        <label className="walkin-field">
          <span>Interne naam (alleen voor ons, staat niet in de e-mail)</span>
          <input
            type="text"
            value={draft.name ?? ''}
            placeholder="Crowdfunding batch 2"
            maxLength={120}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value || null }))}
          />
        </label>
      </section>

      <section className="marketing-panel">
        <div className="marketing-lang-tabs">
          {hasNl ? (
            <button
              className={lang === 'nl' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLang('nl')}
            >
              Nederlands
            </button>
          ) : null}
          {hasEn ? (
            <button
              className={lang === 'en' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLang('en')}
            >
              English
            </button>
          ) : null}
          {!hasEn ? (
            <button
              className="btn-link"
              onClick={() => {
                setDraft((d) => ({ ...d, content: { ...d.content, en: emptyVariant() } }));
                setLang('en');
              }}
            >
              + Engelse variant
            </button>
          ) : null}
          {!hasNl ? (
            <button
              className="btn-link"
              onClick={() => {
                setDraft((d) => ({ ...d, content: { ...d.content, nl: emptyVariant() } }));
                setLang('nl');
              }}
            >
              + Nederlandse variant
            </button>
          ) : null}
          {hasNl && hasEn ? (
            <button className="btn-link voucher-deactivate" onClick={() => removeLang(lang)}>
              {lang === 'nl' ? 'NL-variant verwijderen' : 'EN-variant verwijderen'}
            </button>
          ) : null}
        </div>
        <p className="muted marketing-hint">
          Klanten krijgen de variant van hun eigen taal; ontbreekt die, dan de{' '}
          {draft.default_locale === 'nl' ? 'Nederlandse' : 'Engelse'} variant. Tokens:{' '}
          <code>{'{{voornaam}}'}</code> <code>{'{{achternaam}}'}</code> <code>{'{{email}}'}</code>{' '}
          <code>{'{{unsubscribe_url}}'}</code>.
        </p>

        <div className="marketing-editor-grid">
          <div className="marketing-editor-col">
            <label className="walkin-field">
              <span>Onderwerp ({lang.toUpperCase()})</span>
              <input
                type="text"
                value={variant.subject}
                onChange={(e) => setVariant({ ...variant, subject: e.target.value })}
                placeholder="Nieuw bij Neoseum: …"
              />
            </label>
            <label className="walkin-field">
              <span>Preheader (voorbeeldtekst naast het onderwerp, optioneel)</span>
              <input
                type="text"
                value={variant.preheader ?? ''}
                onChange={(e) => setVariant({ ...variant, preheader: e.target.value || null })}
              />
            </label>

            <div className="marketing-mode-toggle">
              <button
                className={variant.mode === 'blocks' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
                onClick={() => setVariant({ ...variant, mode: 'blocks', blocks: variant.blocks ?? emptyVariant().blocks })}
              >
                Blokken
              </button>
              <button
                className={variant.mode === 'html' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
                onClick={() => setVariant({ ...variant, mode: 'html', html: variant.html ?? '' })}
              >
                HTML plakken
              </button>
            </div>

            {variant.mode === 'blocks' ? (
              <BlocksEditor
                blocks={variant.blocks ?? []}
                onChange={(blocks) => setVariant({ ...variant, blocks })}
              />
            ) : (
              <>
                <textarea
                  className="marketing-html-input"
                  rows={16}
                  value={variant.html ?? ''}
                  placeholder="<html>…</html> — plak hier de volledige e-mail-HTML"
                  onChange={(e) => setVariant({ ...variant, html: e.target.value })}
                  spellCheck={false}
                />
                <p className="muted marketing-hint">
                  Zonder <code>{'{{unsubscribe_url}}'}</code> in je HTML wordt de standaard footer
                  (uitschrijflink + adres) automatisch onderaan toegevoegd.
                </p>
              </>
            )}
          </div>

          <PreviewPane draft={draft} lang={lang} />
        </div>
      </section>

      <section className="marketing-panel">
        <h2>Doelgroep</h2>
        <div className="walkin-row">
          <label className="walkin-field">
            <span>Taal</span>
            <select
              value={draft.audience.locale ?? ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  audience: { ...d.audience, locale: (e.target.value || undefined) as CampaignAudience['locale'] },
                }))
              }
            >
              <option value="">Alle talen</option>
              <option value="nl">Nederlands</option>
              <option value="en">Engels</option>
            </select>
          </label>
          <label className="walkin-field">
            <span>Vestiging</span>
            <select
              value={draft.audience.location_id ?? ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  audience: { ...d.audience, location_id: e.target.value || undefined },
                }))
              }
            >
              <option value="">Alle vestigingen</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.display_name ?? l.name}
                </option>
              ))}
            </select>
          </label>
          <label className="walkin-field">
            <span>Alleen klanten met bezoek sinds (optioneel)</span>
            <input
              type="date"
              value={draft.audience.booked_since ?? ''}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  audience: { ...d.audience, booked_since: e.target.value || undefined },
                }))
              }
            />
          </label>
          <label className="walkin-field">
            <span>Tempo (mails per minuut)</span>
            <input
              type="number"
              min={1}
              max={500}
              value={draft.per_tick}
              onChange={(e) => setDraft((d) => ({ ...d, per_tick: Number(e.target.value) || 60 }))}
            />
          </label>
        </div>
        <ExcludePicker
          campaigns={campaigns}
          currentId={id}
          selected={draft.audience.exclude_campaign_ids ?? []}
          onChange={(ids) =>
            setDraft((d) => ({
              ...d,
              audience: {
                ...d.audience,
                exclude_campaign_ids: ids.length > 0 ? ids : undefined,
              },
            }))
          }
        />
        <SpacingControls
          audience={draft.audience}
          onChange={(patch) => setDraft((d) => ({ ...d, audience: { ...d.audience, ...patch } }))}
        />
        <p className="muted">
          {count == null ? 'Doelgroep tellen…' : audienceCountLine(count, draft.audience)}
        </p>
      </section>

      {error ? <div className="error-card">{error}</div> : null}
      {notice ? <p className="marketing-notice">{notice}</p> : null}

      <div className="walkin-step-actions marketing-actions">
        <button className="btn-link voucher-deactivate" onClick={handleDelete} disabled={busy != null}>
          {id ? 'Verwijderen' : 'Annuleren'}
        </button>
        <button className="btn-secondary" onClick={handleSave} disabled={busy != null}>
          {busy === 'save' ? 'Opslaan…' : 'Opslaan'}
        </button>
        <label className="marketing-test-to">
          <span>Testmail naar</span>
          <input
            type="email"
            value={testTo}
            placeholder={operator?.email ?? 'naam@voorbeeld.nl'}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </label>
        <label className="marketing-test-plain">
          <input
            type="checkbox"
            checked={plainSubject}
            onChange={(e) => setPlainSubject(e.target.checked)}
          />
          <span>zonder [TEST] in onderwerp</span>
        </label>
        <button className="btn-secondary" onClick={handleTest} disabled={busy != null}>
          {busy === 'test' ? 'Versturen…' : `Stuur testmail (${lang.toUpperCase()})`}
        </button>
        <button
          className="walkin-primary-btn"
          onClick={handleStartClick}
          disabled={busy != null || !testSentAt}
          title={testSentAt ? undefined : 'Stuur eerst een testmail'}
        >
          Versturen…
        </button>
      </div>
      {!testSentAt ? (
        <p className="muted marketing-hint">Versturen kan pas na een testmail.</p>
      ) : null}
      <p className="muted marketing-hint">
        In een testmail zijn <code>{'{{voornaam}}'}</code> en <code>{'{{achternaam}}'}</code>{' '}
        voorbeeldwaarden, tenzij het testadres een bestaande klant is. Bij de echte verzending
        krijgt iedere ontvanger zijn eigen naam uit het klantprofiel.
      </p>

      {confirmStart != null ? (
        <div className="dep-modal-backdrop" onClick={() => setConfirmStart(null)}>
          <div className="dep-modal marketing-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dep-modal-head">
              <h2>Campagne versturen?</h2>
              <button className="btn-link dep-modal-close" onClick={() => setConfirmStart(null)}>✕</button>
            </div>
            <div className="dep-modal-body">
              <fieldset className="marketing-spacing-mode">
                <legend>Wanneer?</legend>
                <label>
                  <input type="radio" name="when" checked={when === 'now'} onChange={() => setWhen('now')} />{' '}
                  Nu versturen
                </label>
                <label>
                  <input type="radio" name="when" checked={when === 'later'} onChange={() => setWhen('later')} />{' '}
                  Inplannen op
                </label>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => {
                    setScheduleAt(e.target.value);
                    setWhen('later');
                  }}
                />
              </fieldset>
              {when === 'now' ? (
                <>
                  <p>
                    “{displaySubject}” gaat nu naar <strong>{confirmStart.ready} ontvangers</strong>{' '}
                    ({audienceLabel(draft.audience, locations)}), in porties van {draft.per_tick} per minuut.
                  </p>
                  {confirmStart.count > confirmStart.ready ? (
                    <p className="muted">{heldLine(confirmStart, draft.audience)}</p>
                  ) : null}
                </>
              ) : (
                <p>
                  “{displaySubject}” start op het gekozen moment ({audienceLabel(draft.audience, locations)}).
                  Vandaag zouden er {confirmStart.ready} direct gaan; wie er precies mail krijgt, wordt
                  op dat moment bepaald, met dezelfde wachtregels. Tot die tijd kun je de planning
                  nog terugdraaien.
                </p>
              )}
              <p className="muted">Dit kan niet ongedaan worden gemaakt zodra mails de deur uit zijn.</p>
              <div className="walkin-step-actions">
                <button className="btn-secondary" onClick={() => setConfirmStart(null)} disabled={busy === 'start'}>
                  Terug
                </button>
                <button className="walkin-primary-btn" onClick={handleStartConfirmed} disabled={busy === 'start'}>
                  {busy === 'start'
                    ? 'Starten…'
                    : when === 'later'
                      ? 'Ja, inplannen'
                      : `Ja, verstuur naar ${confirmStart.ready} ontvangers`}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Which earlier campaigns' recipients to skip. This is what makes batching
// possible: booked_since is a lower bound, so a wider date window always
// contains the previous batch, and only an explicit exclusion can express
// "everyone who has not had this yet".
function ExcludePicker({
  campaigns,
  currentId,
  selected,
  onChange,
}: {
  campaigns: CampaignListRow[];
  currentId: string | null;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  // Only campaigns that actually mailed someone can exclude anyone.
  const candidates = campaigns.filter(
    (c) => c.id !== currentId && c.sent_count > 0,
  );
  if (candidates.length === 0) return null;

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="marketing-exclude">
      <span className="marketing-block-type">Sla ontvangers over van</span>
      {candidates.map((c) => (
        <label key={c.id} className="marketing-exclude-row">
          <input
            type="checkbox"
            checked={selected.includes(c.id)}
            onChange={() => toggle(c.id)}
          />
          <span>
            {c.name ?? c.subject}{' '}
            <span className="muted">
              ({c.sent_count} verzonden{c.started_at ? `, ${formatVenueDateTime(c.started_at)}` : ''})
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live preview (server-rendered, sandboxed iframe)
// ---------------------------------------------------------------------------
function PreviewPane({ draft, lang }: { draft: CampaignDraftInput; lang: CampaignLocale }) {
  const [html, setHtml] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  // 'render' = iframe; 'raw' = de exacte HTML-broncode zoals die verstuurd
  // wordt (tokens al ingevuld met de voorbeeldontvanger).
  // 'text' = de platte-tekstversie (multipart/alternative) die mailclients
  // tonen als ze geen HTML renderen.
  const [tab, setTab] = useState<'render' | 'text' | 'raw'>('render');
  const seq = useRef(0);

  useEffect(() => {
    const problem = draftProblem(draft);
    if (problem) {
      setState('idle');
      setHtml('');
      setText('');
      setSubject('');
      return;
    }
    setState('loading');
    const mySeq = ++seq.current;
    const t = setTimeout(async () => {
      const clean = cleanDraft(draft);
      const res = await previewCampaign(clean.content, lang, clean.default_locale);
      if (seq.current !== mySeq) return; // verouderd antwoord
      if (res.ok && res.data) {
        setHtml(res.data.html);
        setText(res.data.text);
        setSubject(res.data.subject);
        setState('idle');
      } else {
        setState('error');
      }
    }, 600);
    return () => clearTimeout(t);
  }, [draft, lang]);

  return (
    <div className="marketing-preview">
      <div className="marketing-preview-head">
        <span className="marketing-block-type">Voorbeeld ({lang.toUpperCase()})</span>
        <span className="marketing-block-tools">
          {state === 'loading' ? <span className="muted small">renderen…</span> : null}
          {state === 'error' ? <span className="muted small">voorbeeld niet beschikbaar</span> : null}
          <button
            className={tab === 'render' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
            onClick={() => setTab('render')}
          >
            Weergave
          </button>
          <button
            className={tab === 'text' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
            onClick={() => setTab('text')}
          >
            Tekstversie
          </button>
          <button
            className={tab === 'raw' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
            onClick={() => setTab('raw')}
          >
            Broncode
          </button>
        </span>
      </div>
      {subject ? <div className="marketing-preview-subject">{subject}</div> : null}
      {html && tab === 'render' ? <ScaledEmailFrame html={html} /> : null}
      {html && tab === 'text' ? (
        <pre className="marketing-preview-raw marketing-preview-text">{text}</pre>
      ) : null}
      {html && tab === 'raw' ? (
        <pre className="marketing-preview-raw">{html}</pre>
      ) : null}
      {!html ? (
        <div className="marketing-preview-empty muted">
          Vul onderwerp en inhoud in om het voorbeeld te zien.
        </div>
      ) : null}
    </div>
  );
}

// E-mails zijn 600px + ~64px achtergrondmarge breed; schaal het frame naar de
// beschikbare breedte zodat het hele ontwerp zichtbaar is zonder horizontale
// scrollbar. Hoogte blijft vast; verticaal scrollt de mail in het frame zelf.
const EMAIL_DESIGN_WIDTH = 664;
const PREVIEW_HEIGHT = 720;

export function ScaledEmailFrame({ html }: { html: string }) {
  const wrap = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / EMAIL_DESIGN_WIDTH));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrap} className="marketing-preview-scalewrap" style={{ height: PREVIEW_HEIGHT }}>
      <iframe
        className="marketing-preview-frame"
        title="E-mail voorbeeld"
        sandbox=""
        srcDoc={html}
        style={{
          width: EMAIL_DESIGN_WIDTH,
          height: PREVIEW_HEIGHT / scale,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blocks editor
// ---------------------------------------------------------------------------
export function BlocksEditor({
  blocks,
  onChange,
}: {
  blocks: CampaignBlock[];
  onChange: (b: CampaignBlock[]) => void;
}) {
  const update = (i: number, b: CampaignBlock) => {
    const next = blocks.slice();
    next[i] = b;
    onChange(next);
  };
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = blocks.slice();
    const tmp = next[i]!;
    next[i] = next[j]!;
    next[j] = tmp;
    onChange(next);
  };
  const add = (b: CampaignBlock) => onChange([...blocks, b]);

  return (
    <div className="marketing-blocks">
      {blocks.map((b, i) => (
        <div key={i} className="marketing-block">
          <div className="marketing-block-head">
            <span className="marketing-block-type">
              {b.type === 'heading' ? 'Kop' : b.type === 'paragraph' ? 'Tekst' : b.type === 'image' ? 'Afbeelding' : 'Knop'}
            </span>
            <span className="marketing-block-tools">
              <button className="btn-link" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
              <button className="btn-link" onClick={() => move(i, 1)} disabled={i === blocks.length - 1}>↓</button>
              <button className="btn-link voucher-deactivate" onClick={() => remove(i)}>✕</button>
            </span>
          </div>
          {b.type === 'heading' ? (
            <input
              type="text"
              value={b.text}
              placeholder="Kop"
              onChange={(e) => update(i, { ...b, text: e.target.value })}
            />
          ) : null}
          {b.type === 'paragraph' ? (
            <textarea
              rows={3}
              value={b.text}
              placeholder="Tekst…"
              onChange={(e) => update(i, { ...b, text: e.target.value })}
            />
          ) : null}
          {b.type === 'image' ? (
            <div className="walkin-row">
              <input
                type="text"
                value={b.url}
                placeholder="https://… of {{token}}"
                onChange={(e) => update(i, { ...b, url: e.target.value })}
              />
              <input
                type="text"
                value={b.alt ?? ''}
                placeholder="Alt-tekst"
                onChange={(e) => update(i, { ...b, alt: e.target.value })}
              />
            </div>
          ) : null}
          {b.type === 'button' ? (
            <div className="walkin-row">
              <input
                type="text"
                value={b.label}
                placeholder="Knoptekst (bijv. Boek nu)"
                onChange={(e) => update(i, { ...b, label: e.target.value })}
              />
              <input
                type="text"
                value={b.url}
                placeholder="https://… of {{google review}}"
                onChange={(e) => update(i, { ...b, url: e.target.value })}
              />
              <input
                type="text"
                value={b.icon ?? ''}
                placeholder="Icoon (optioneel): {{google icon}}"
                onChange={(e) =>
                  update(i, { ...b, icon: e.target.value || undefined })}
              />
            </div>
          ) : null}
          {b.type === 'button' ? (
            <p className="muted small">
              Link en icoon mogen een token zijn. Levert de link niets op — een vestiging
              zonder Tripadvisor-pagina bijvoorbeeld — dan valt deze knop vanzelf weg bij
              die ontvanger, dus één mail volstaat voor alle vestigingen. Het icoon is
              versiering: de knoptekst blijft altijd staan, ook als de mailclient
              afbeeldingen blokkeert.
            </p>
          ) : null}
        </div>
      ))}
      <div className="marketing-block-add">
        <button className="btn-secondary" onClick={() => add({ type: 'heading', text: '' })}>+ Kop</button>
        <button className="btn-secondary" onClick={() => add({ type: 'paragraph', text: '' })}>+ Tekst</button>
        <button className="btn-secondary" onClick={() => add({ type: 'image', url: '', alt: '' })}>+ Afbeelding</button>
        <button className="btn-secondary" onClick={() => add({ type: 'button', label: '', url: 'https://neoseum.com' })}>+ Knop</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail (sending / sent / cancelled)
// ---------------------------------------------------------------------------
function CampaignDetailView({
  campaignId,
  locations,
  onClose,
  onEdit,
}: {
  campaignId: string;
  locations: BookingLocation[];
  onClose: () => void;
  onEdit: (id: string) => void;
}) {
  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [held, setHeld] = useState<CampaignHeld | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    const res = await getCampaign(campaignId);
    if (res.ok && res.data) {
      setCampaign(res.data.campaign);
      setStats(res.data.stats);
      setHeld(res.data.held ?? null);
    } else {
      setError(errText(res.error));
    }
  }, [campaignId]);

  // Poll elke 5s zolang de campagne verstuurt.
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (campaign?.status !== 'sending') return;
    timer.current = window.setInterval(() => void load(), 5000);
    return () => {
      if (timer.current != null) window.clearInterval(timer.current);
    };
  }, [campaign?.status, load]);

  const handleCancel = async () => {
    if (!window.confirm('Campagne stoppen? Al verzonden mails blijven verzonden; de rest wordt niet meer verstuurd.')) return;
    const res = await cancelCampaign(campaignId);
    if (res.ok) void load();
    else setError(errText(res.error));
  };

  const handleUnschedule = async () => {
    const res = await unscheduleCampaign(campaignId);
    if (res.ok) onEdit(campaignId);
    else setError(errText(res.error));
  };

  if (!campaign) {
    return (
      <div className="screen marketing-screen">
        {error ? <div className="error-card">{error}</div> : <p className="muted">Laden…</p>}
      </div>
    );
  }

  const progressPct = campaign.recipient_count > 0
    ? Math.round(((campaign.sent_count + campaign.skipped_count + campaign.failed_count) / campaign.recipient_count) * 100)
    : 0;

  return (
    <div className="screen marketing-screen">
      <div className="screen-header">
        <h1>{campaign.name ?? campaign.subject}</h1>
        {campaign.name ? <p className="muted">{campaign.subject}</p> : null}
      </div>
      <button className="btn-link back-link" onClick={onClose}>← Terug naar overzicht</button>

      <section className="marketing-panel">
        <p>
          <span className={`pill marketing-pill-${campaign.status}`}>{STATUS_LABEL[campaign.status] ?? campaign.status}</span>{' '}
          <span className="muted">
            {audienceLabel(campaign.audience, locations)} · {Object.keys(campaign.content).map((l) => l.toUpperCase()).join(' + ')}
          </span>
        </p>
        {campaign.status === 'scheduled' ? (
          <>
            <p>
              Start op <strong>{campaign.scheduled_at ? formatVenueDateTime(campaign.scheduled_at) : '—'}</strong>.
              Wie er mail krijgt, wordt op dat moment bepaald.
            </p>
            <div className="walkin-step-actions">
              <button className="btn-secondary" onClick={handleUnschedule}>Terug naar concept</button>
              <button className="btn-link voucher-deactivate" onClick={handleCancel}>Annuleren</button>
            </div>
          </>
        ) : campaign.status === 'sending' ? (
          <>
            <div className="marketing-progress">
              <div className="marketing-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="muted">
              {campaign.sent_count} verzonden van {campaign.recipient_count}
              {campaign.skipped_count ? ` · ${campaign.skipped_count} overgeslagen` : ''}
              {campaign.failed_count ? ` · ${campaign.failed_count} mislukt` : ''}
              {' '}(~{campaign.per_tick}/min)
            </p>
            <CampaignHeldNote held={held} />
            <button className="btn-secondary" onClick={handleCancel}>Stop campagne</button>
          </>
        ) : (
          <p className="muted">
            {campaign.sent_count} verzonden van {campaign.recipient_count}
            {campaign.skipped_count ? ` · ${campaign.skipped_count} overgeslagen` : ''}
            {campaign.failed_count ? ` · ${campaign.failed_count} mislukt` : ''}
            {campaign.completed_at ? ` · afgerond ${formatVenueDateTime(campaign.completed_at)}` : ''}
          </p>
        )}
        {campaign.status !== 'sending' ? <CampaignHeldNote held={held} /> : null}
      </section>

      {stats ? (
        <section className="marketing-panel">
          <h2>Resultaat</h2>
          <div className="marketing-stats">
            <StatTile label="Afgeleverd" value={stats.delivered} />
            <StatTile label="Geopend" value={stats.opened} />
            <StatTile label="Geklikt" value={stats.clicked} />
            <StatTile label="Gebounced" value={stats.bounced} warn={stats.bounced > 0} />
            <StatTile label="Spamklachten" value={stats.complained} warn={stats.complained > 0} />
          </div>
          <p className="muted marketing-hint">
            Cijfers komen binnen via Resend en lopen enkele minuten achter. Klachten en bounces
            worden automatisch uitgeschreven/onderdrukt bij volgende campagnes.
          </p>
        </section>
      ) : null}

      {error ? <div className="error-card">{error}</div> : null}
    </div>
  );
}

function StatTile({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <div className={warn ? 'marketing-stat marketing-stat-warn' : 'marketing-stat'}>
      <div className="marketing-stat-value">{value}</div>
      <div className="marketing-stat-label">{label}</div>
    </div>
  );
}
